<div class="order-details-dialog">
  <!-- Navigation Arrow - Previous -->
  @if (ordersList().length > 0) {
    <button class="nav-arrow nav-arrow-prev"
            [class.disabled]="!canGoPrevious()"
            [disabled]="!canGoPrevious() || loading()"
            (click)="goToPreviousOrder()">
      <mat-icon>chevron_left</mat-icon>
    </button>
  }

  <!-- Navigation Arrow - Next -->
  @if (ordersList().length > 0) {
    <button class="nav-arrow nav-arrow-next"
            [class.disabled]="!canGoNext()"
            [disabled]="!canGoNext() || loading()"
            (click)="goToNextOrder()">
      <mat-icon>chevron_right</mat-icon>
    </button>
  }

  <!-- Header -->
  <header class="od-head">
    <div class="od-head-left">
      <div class="od-eyebrow">{{ 'ORDERS.ORDER' | translate }}</div>
      <h1 class="od-title">#{{ data.order.orderId || data.order.id }}</h1>
    </div>
    @if (!loading() && orderDetails()) {
      @if (isUrgentOrder()) {
        <span class="od-badge urgent"><span class="dot"></span>{{ 'ORDERS.URGENT' | translate }}</span>
      }
      <span class="od-badge" [class]="'od-badge ' + getStatusClass($any(orderDetails()).status)">
        <span class="dot"></span>{{ getStatusLabel($any(orderDetails()).status) }}
      </span>
    }
    <button class="od-close" (click)="close()" [attr.aria-label]="'COMMON.CLOSE' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <!-- Loading -->
  @if (loading()) {
    <div class="loading">
      <img src="assets/logos/step2-loading-animation_3.svg" [alt]="'COMMON.LOADING' | translate" class="loading-logo">
    </div>
  }

  <!-- No data -->
  @if (!loading() && !orderDetails()) {
    <div class="od-body">
      <p>{{ 'ORDERS.LOAD_ERROR' | translate }}</p>
    </div>
  }

  <!-- Content -->
  @if (!loading() && orderDetails()) {
    <div class="od-body">

      <!-- CLIENT & SHIPPING -->
      <div class="od-card od-info-card">
        <div class="od-info-top">
          <div>
            <div class="od-info-name">{{ $any(orderDetails()).shipmentAddress?.name || '-' }}</div>
            <div class="od-info-created">{{ $any(orderDetails()).orderDate || '-' }}</div>
          </div>
        </div>
        <div class="od-info-grid">
          <div class="od-field">
            <div class="k">{{ 'ORDERS.STREET_DOOR' | translate }}</div>
            <div class="v">{{ $any(orderDetails()).shipmentAddress?.address || '-' }}{{ $any(orderDetails()).shipmentAddress?.door ? ', ' + $any(orderDetails()).shipmentAddress?.door : '' }}</div>
          </div>
          <div class="od-field">
            <div class="k">{{ 'ORDERS.SHIPPING.PHONE' | translate }}</div>
            <div class="v">{{ $any(orderDetails()).shipmentAddress?.phone || '-' }}</div>
          </div>
          <div class="od-field">
            <div class="k">{{ 'ORDERS.SHIPPING.POSTAL_CODE' | translate }} · {{ 'ORDERS.SHIPPING.CITY' | translate }}</div>
            <div class="v">{{ $any(orderDetails()).shipmentAddress?.postalCode || '-' }} {{ $any(orderDetails()).shipmentAddress?.local || '-' }}</div>
          </div>
          <div class="od-field">
            <div class="k">{{ 'ORDERS.SHIPPING.TYPE' | translate }}</div>
            <div class="v">{{ $any(orderDetails()).shipmentType?.name || '-' }}</div>
          </div>
        </div>
        @if (getEstimatedDeliveryDate(); as estDate) {
          <div class="od-ship-note">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
            <span>{{ 'ORDERS.ESTIMATED_SHIPPING_DATE' | translate }}: <b>{{ estDate }}</b>@if (showEstimateNote()) { <em class="estimate-note">{{ 'ORDERS.ESTIMATED_SHIPPING_DATE_NOTE' | translate }}</em>}</span>
          </div>
        }
      </div>

      <!-- ORDER ITEM GROUPS -->
      @if ($any(orderDetails()).groups && $any(orderDetails()).groups.length > 0) {
        @for (group of $any(orderDetails()).groups; track $any(group).id || $index; let groupIdx = $index) {
          <div class="od-card od-item" [class.collapsed]="!isGroupExpanded(groupIdx)">
            <!-- Item Header (Expandable) -->
            <div class="od-item-head" (click)="toggleGroupFiles(groupIdx)">
              <div class="od-item-thumb">
                @if (getGroupCoverThumbnail(groupIdx)) {
                  <img [src]="getGroupCoverThumbnail(groupIdx)!" [alt]="'PRODUCTS.COVER' | translate" />
                } @else {
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v16H4z"/><path d="M12 4v16"/></svg>
                }
              </div>
              <div class="od-item-titles">
                <div class="t">{{ getGroupDescription(group) }}</div>
                @if (getGroupName(group)) {
                  <div class="s">{{ getGroupName(group) }}</div>
                }
                @if (isGroupShowcase(group)) {
                  <span class="od-showcase-badge">{{ 'ORDERS.SHOWCASE' | translate }}</span>
                }
              </div>
              <div class="od-item-price">
                <div class="ref">{{ getOrderItemGroupId(group) }}</div>
                @if (pricesVisible()) {
                  <div class="p">{{ formatPrice($any(group).price) }}</div>
                }
              </div>
              <svg class="od-chev" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </div>

            <!-- Item Body (Expanded) -->
            @if (isGroupExpanded(groupIdx)) {
              <div class="od-item-body">
                @if (isAlbumGroup(group)) {
                  <div class="od-item-stats">
                    @if (getGroupName(group)) {
                      <div class="od-stat"><div class="k">{{ 'ORDERS.SHIPPING.NAME' | translate }}</div><div class="v">{{ getGroupName(group) }}</div></div>
                    }
                    <div class="od-stat"><div class="k">{{ 'ORDERS.NUM_PAGES' | translate }}</div><div class="v">{{ getGroupPages(group) }}</div></div>
                  </div>
                }

                <!-- Product lines -->
                @if ($any(group).products && $any(group).products.length > 0) {
                  @for (product of $any(group).products; track $any(product).id || $index) {
                    <div class="od-line">
                      <div class="od-line-top">
                        @if (canRepairOrderItems && $any(product).projectInfo) {
                          <app-order-item-repair-menu
                            class="product-repair-menu"
                            [orderItemId]="$any(product).orderItemId || $any(product).itemId || $any(product).id"
                            [projectInfo]="$any(product).projectInfo"
                            [apiVersion]="$any(orderDetails())?.apiVersion"
                            (repaired)="loadOrderDetails()">
                          </app-order-item-repair-menu>
                        }
                        <div class="od-line-name">{{ getProductFullName(product) }} <span class="od-line-ref">{{ getProductItemId(product) }}</span></div>
                        <div class="od-line-qty">×{{ $any(product).qty }}</div>
                        @if (pricesVisible()) {
                          <div class="od-line-price">{{ formatPrice($any(product).price) }}</div>
                        }
                      </div>
                      @if ($any(product).finishes && $any(product).finishes.length > 0) {
                        <div class="od-specs">
                          @for (finish of getSortedFinishes($any(product)); track $index) {
                            <div class="od-spec">
                              <span class="sk">{{ getFinishOptionName(finish) }}</span>
                              <span class="dots"></span>
                              <span class="sv" [class.finish-value-stamping-text]="isStampingTextValue(finish)">{{ getFinishValue(finish) }}</span>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                }
                @for (ev of getGroupExtraValues(group); track $index) {
                  <div class="od-line">
                    <div class="od-line-top">
                      <div class="od-line-name">{{ 'ORDERS.EXTRAS' | translate }} <span class="od-breadcrumb">› {{ getExtraValueRef(ev) }}</span></div>
                      @if (pricesVisible()) {
                        <div class="od-line-price">{{ formatPrice(getExtraValuePrice(ev)) }}</div>
                      }
                    </div>
                  </div>
                }

                <!-- Observations -->
                @if ($any(group).observations) {
                  <div class="od-observations">
                    <span class="ok">{{ 'ORDERS.OBSERVATIONS' | translate }}:</span>
                    <span class="ov">{{ $any(group).observations }}</span>
                  </div>
                }

                <!-- FILES (nested collapsible: project previews + direct files) -->
                @if (getGroupThumbnails(groupIdx).length > 0 || groupHasFiles(group)) {
                  <div class="od-nested-files" [class.collapsed]="!isFilesExpanded(groupIdx)">
                    <div class="od-sec-head" (click)="toggleFilesExpanded(groupIdx); $event.stopPropagation()">
                      <div class="s-icon">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><circle cx="9" cy="12" r="1.6"/><path d="m6 17 3-3 3 3 4-4 3 3"/></svg>
                      </div>
                      <div class="s-title">{{ 'ORDERS.SHOW_FILES' | translate }}</div>
                      <svg class="od-chev" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    </div>
                    @if (isFilesExpanded(groupIdx)) {
                      <div class="od-sec-body">

                        <!-- Project previews (album pages / vellum / cover from draft) -->
                        @for (thumbGroup of getGroupThumbnails(groupIdx); track $index; let tgIdx = $index) {
                          <div class="od-file-group">
                            <div class="od-fg-head">
                              <svg class="fg-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                              <span class="fg-title">{{ thumbGroup.groupName }}</span>
                              <span class="fg-count">{{ thumbGroup.thumbnails.length }}</span>
                            </div>
                            <!-- Multi-design drafts: this design's finishes vs the common base -->
                            @if (thumbGroup.details?.length) {
                              <div class="od-fg-details">
                                @for (detail of thumbGroup.details; track $index) {
                                  <span class="od-fg-detail" [class.same]="!detail.value">
                                    {{ detail.label }}@if (detail.value) {: <b>{{ detail.value }}</b>}
                                  </span>
                                }
                              </div>
                            }
                            <div class="od-thumb-grid">
                              @for (thumb of thumbGroup.thumbnails; track $index; let thumbIdx = $index) {
                                <div class="od-page" (click)="openThumbnailViewer(groupIdx, tgIdx, thumbIdx)">
                                  <div class="canvas">
                                    <img [src]="thumb.dataUrl" [alt]="thumb.label" loading="lazy" />
                                  </div>
                                  <div class="cap">{{ thumb.label }}</div>
                                </div>
                              }
                            </div>
                          </div>
                        }

                        <!-- Direct order files per product -->
                        @if ($any(group).products && $any(group).products.length > 0) {
                          @for (product of $any(group).products; track $index) {
                            @if (getProductFiles(product).length > 0) {
                              <div class="od-file-group">
                                <div class="od-fg-head">
                                  <svg class="fg-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                                  <span class="fg-title">{{ getProductFullName(product) }}</span>
                                  <span class="fg-count">{{ getProductFiles(product).length }}</span>
                                </div>
                                @for (fileGroup of getGroupedProductFiles(product); track $index) {
                                  @if (fileGroup.header) {
                                    <div class="od-files-header">{{ fileGroup.header }}</div>
                                  }
                                  <div class="files-grid">
                                    @for (file of fileGroup.files; track $index; let fileIdx = $index) {
                                      <div class="file-item">
                                        <div class="file-thumbnail">
                                          @if (isImageFile(file)) {
                                            <img [src]="getFileImageUrl(file, true)"
                                                 [alt]="$any(file).name || ('ORDERS.FILE' | translate)"
                                                 loading="lazy"
                                                 (error)="onThumbnailError($event, file)">
                                          } @else if (getVectorThumb(file); as vectorThumb) {
                                            <!-- Vector file (.ai/.pdf/.eps): persisted render thumb + extension badge -->
                                            <img [src]="vectorThumb"
                                                 [alt]="$any(file).name || ('ORDERS.FILE' | translate)"
                                                 loading="lazy"
                                                 (error)="onVectorThumbError(file)">
                                            <span class="vector-ext-badge">.{{ getFileExtension(file) }}</span>
                                          } @else {
                                            <div class="file-placeholder">
                                              <mat-icon>{{ getFileIcon(file) }}</mat-icon>
                                              <span class="file-ext">.{{ getFileExtension(file) }}</span>
                                            </div>
                                          }
                                          @if ($any(file).qty && $any(file).qty > 1) {
                                            <div class="file-qty-badge">x{{ $any(file).qty }}</div>
                                          }
                                          <!-- Hover action buttons -->
                                          <div class="file-actions">
                                            <button class="file-action-btn preview"
                                                    type="button"
                                                    (click)="openImageViewer(groupIdx, file); $event.stopPropagation()"
                                                    [matTooltip]="'COMMON.VIEW' | translate">
                                              <mat-icon>search</mat-icon>
                                            </button>
                                            <button class="file-action-btn download"
                                                    type="button"
                                                    (click)="downloadFile(file); $event.stopPropagation()"
                                                    [matTooltip]="'COMMON.DOWNLOAD' | translate">
                                              <mat-icon>download</mat-icon>
                                            </button>
                                          </div>
                                        </div>
                                        <span class="file-name">{{ $any(file).name || ('ORDERS.IMAGE' | translate) }}</span>
                                      </div>
                                    }
                                  </div>
                                }
                              </div>
                            }
                          }
                        }

                      </div>
                    }
                  </div>
                }

              </div>
            }
          </div>
        }
      }

      <!-- Order-level extras as a standalone "fake group" card -->
      @if (getOrderExtras().length > 0) {
        <div class="od-card od-item" [class.collapsed]="!extrasExpanded()">
          <div class="od-item-head" (click)="toggleExtrasExpanded()">
            <div class="od-item-thumb extras">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
            </div>
            <div class="od-item-titles">
              <div class="t">{{ 'ORDERS.EXTRAS' | translate }}</div>
              @if (getOrderExtrasNames()) {
                <div class="s">{{ getOrderExtrasNames() }}</div>
              }
            </div>
            <div class="od-item-price">
              @if (pricesVisible()) {
                <div class="p">{{ formatPrice(getOrderExtrasTotal()) }}</div>
              }
            </div>
            <svg class="od-chev" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
          </div>
          @if (extrasExpanded()) {
            <div class="od-item-body">
              @for (extra of getOrderExtras(); track $index) {
                <div class="od-line">
                  <div class="od-line-top">
                    <div class="od-line-name">{{ getOrderExtraName(extra) }}</div>
                    @if (pricesVisible()) {
                      <div class="od-line-price">{{ formatPrice(getOrderExtraPrice(extra)) }}</div>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Vouchers -->
      @if (pricesVisible() && getAppliedVouchers().length > 0) {
        <div class="od-card od-vouchers">
          <div class="od-vouchers-title">{{ 'ORDERS.APPLIED_VOUCHERS' | translate }}</div>
          @for (voucher of getAppliedVouchers(); track voucher.voucherId) {
            <div class="od-voucher-row">
              <span class="voucher-code">{{ voucher.voucherId }}</span>
              @if (voucher.statusKey === 'REDEEMED_SHIPPING') {
                <span class="voucher-tag">{{ 'ORDERS.TOTALS.SHIPPING_DISCOUNT' | translate }}</span>
              }
              <span class="voucher-value">{{ formatPrice(voucher.value) }}</span>
            </div>
          }
        </div>
      }

      <!-- PAYMENT + TOTALS — totals-boxes design (designs/toolbar/totals-boxes.html):
           head (payment method + status chip), full-width payment instructions,
           then the Produtos/Portes boxes band and the VAT + grand-total row. -->
      <div class="od-card od-totals-card">
        <!-- paymentTypeId 0 (ref "sem_pagamento") marks a zero-total order
             placed without payment: nothing to show or pay, so the payment
             head + instructions are hidden entirely (totals below stay). -->
        @if ($any(orderDetails()).payment && +$any(orderDetails()).payment.id !== 0) {
          <div class="od-totals-head">
            <h3>{{ $any(orderDetails()).payment.name || ('ORDERS.CURRENT_ACCOUNT' | translate) }}</h3>
            <span class="od-paychip" [class.confirmed]="+$any(orderDetails()).status >= 3">
              @if (+$any(orderDetails()).status >= 3) {
                <span class="ic"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
                {{ 'ORDERS.PAYMENT_CONFIRMED' | translate }}
              } @else if (+$any(orderDetails()).status === 2) {
                <mat-icon class="payment-status-icon">hourglass_top</mat-icon>
                {{ 'ORDERS.AWAITING_PAYMENT_STATUS' | translate }}
              } @else {
                <mat-icon class="payment-status-icon">hourglass_top</mat-icon>
                {{ 'ORDERS.PENDING_STATUS' | translate }}
              }
            </span>
          </div>

            <!-- Payment instructions + pay buttons — CART module only
                 (guide §4.2): an ORDERS-only sub-user views but can't pay.
                 Full-width row so bank/Multibanco tables never stack against
                 the totals column like in the old two-column layout. -->
            @if (perms.canCart()) {
            <div class="od-totals-payment">
            @if (bankTransferLoading()) {
              <div class="payment-bank-loading">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            } @else if (bankTransferConfig()) {
              <app-bank-transfer-details
                class="payment-bank-details"
                [config]="bankTransferConfig()!"
                [isEU]="bankTransferIsEU()"
                [amount]="$any(orderDetails()).totalValue"
                [currencyId]="$any(orderDetails()).currencyId"
                [compact]="true">
              </app-bank-transfer-details>
            } @else if (bankTransferError()) {
              <div class="payment-bank-error">
                <span>{{ 'PAYMENT_PROCESSING.BANK_TRANSFER.LOAD_ERROR' | translate }}</span>
                <button mat-stroked-button color="primary" (click)="fetchBankTransferConfig()">
                  <mat-icon>refresh</mat-icon>
                  {{ 'COMMON.RETRY' | translate }}
                </button>
              </div>
            }

            @if (multibancoLoading()) {
              <div class="payment-bank-loading">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            } @else if (multibancoConfig()) {
              <app-multibanco-details
                class="payment-bank-details"
                [config]="multibancoConfig()!"
                [currencyId]="$any(orderDetails()).currencyId"
                [compact]="true">
              </app-multibanco-details>
            } @else if (multibancoError()) {
              <div class="payment-bank-error">
                <span>{{ 'PAYMENT_PROCESSING.MULTIBANCO.LOAD_ERROR' | translate }}</span>
                <button mat-stroked-button color="primary" (click)="fetchMultibancoConfig()">
                  <mat-icon>refresh</mat-icon>
                  {{ 'COMMON.RETRY' | translate }}
                </button>
              </div>
            }

            @if (boletoLoading()) {
              <div class="payment-bank-loading">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            } @else if (boletoConfig()) {
              <app-boleto-details
                class="payment-bank-details"
                [config]="boletoConfig()!"
                [compact]="true">
              </app-boleto-details>
            } @else if (boletoError()) {
              <div class="payment-bank-error">
                <span>{{ 'PAYMENT_PROCESSING.BOLETO.LOAD_ERROR' | translate }}</span>
                <button mat-stroked-button color="primary" (click)="fetchBoletoConfig()">
                  <mat-icon>refresh</mat-icon>
                  {{ 'COMMON.RETRY' | translate }}
                </button>
              </div>
            }

            @if (showPaypalButton()) {
              @if (paypalError()) {
                <div class="payment-bank-error">
                  <span>{{ 'PAYMENT_PROCESSING.ERROR_DESC' | translate }}</span>
                  <button mat-stroked-button color="primary" (click)="retryPaypal()">
                    <mat-icon>refresh</mat-icon>
                    {{ 'COMMON.RETRY' | translate }}
                  </button>
                </div>
              } @else {
                <app-paypal-gateway
                  class="payment-paypal"
                  [orderId]="embeddedOrderId()"
                  [currency]="paypalCurrencyCode()"
                  (completed)="onPaypalCompleted()"
                  (failed)="onPaypalFailed($event)" />
              }
            }

            @if (showBraintreeButton()) {
              <div class="payment-braintree">
                <button mat-flat-button color="primary" class="payment-braintree-btn"
                        (click)="openBraintreeDialog()">
                  <mat-icon>credit_card</mat-icon>
                  {{ 'PAYMENT_PROCESSING.BRAINTREE.PAY_WITH_CARD' | translate }}
                </button>
                <p class="payment-braintree-hint">
                  {{ 'PAYMENT_PROCESSING.BRAINTREE.CLICK_TO_PAY' | translate }}
                </p>
              </div>
            }

            <!-- Cielo: checkout URL is prefetched, so this is a plain
                 target=_blank link — the checkout opens in a NEW TAB and the
                 app stays on the order (the customer can't get lost even if
                 Cielo's return redirect is dropped). -->
            @if (cieloLoading()) {
              <div class="payment-bank-loading">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            } @else if (cieloUrl()) {
              <div class="payment-braintree">
                <a mat-flat-button color="primary" class="payment-braintree-btn"
                   [href]="cieloUrl()" target="_blank" rel="noopener"
                   (click)="onCieloOpened()">
                  <mat-icon>open_in_new</mat-icon>
                  {{ 'PAYMENT_PROCESSING.REDIRECT.PAY_NOW' | translate }}
                </a>
                <p class="payment-braintree-hint">
                  {{ 'PAYMENT_PROCESSING.REDIRECT.NEW_TAB_HINT' | translate }}
                </p>
              </div>
            } @else if (cieloError()) {
              <div class="payment-bank-error">
                <span>{{ 'PAYMENT_PROCESSING.ERROR_DESC' | translate }}</span>
                <button mat-stroked-button color="primary" (click)="fetchCieloConfig()">
                  <mat-icon>refresh</mat-icon>
                  {{ 'COMMON.RETRY' | translate }}
                </button>
              </div>
            }

            <!-- Viva Wallet: prefetched order code; the Smart Checkout opens
                 in a POPUP and the in-app return page relays the outcome
                 back here. On failure the dead code is aborted and a fresh
                 one minted; if the order is validating, a processing note
                 shows instead of a retry (double-charge risk). -->
            @if (vivaLoading() || vivaConfirming() || vivaAborting()) {
              <div class="payment-bank-loading">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            } @else if (vivaProcessing()) {
              <div class="payment-viva-processing">
                <mat-icon>hourglass_top</mat-icon>
                <span>{{ 'PAYMENT_PROCESSING.RESULT.PROCESSING_DESC' | translate }}</span>
              </div>
            } @else if (vivaOrderCode()) {
              <div class="payment-braintree">
                @if (vivaRetry()) {
                  <div class="payment-viva-failed">
                    <mat-icon>error</mat-icon>
                    <span>{{ 'PAYMENT_PROCESSING.REDIRECT.RETRY_HINT' | translate }}</span>
                  </div>
                }
                <button mat-flat-button color="primary" class="payment-braintree-btn"
                        (click)="openVivaCheckout()">
                  <mat-icon>open_in_new</mat-icon>
                  {{ 'PAYMENT_PROCESSING.REDIRECT.PAY_NOW' | translate }}
                </button>
                <p class="payment-braintree-hint">
                  {{ 'PAYMENT_PROCESSING.REDIRECT.NEW_TAB_HINT' | translate }}
                </p>
              </div>
            } @else if (vivaError()) {
              <div class="payment-bank-error">
                <span>{{ 'PAYMENT_PROCESSING.ERROR_DESC' | translate }}</span>
                <button mat-stroked-button color="primary" (click)="retryVivaAfterFailure()">
                  <mat-icon>refresh</mat-icon>
                  {{ 'COMMON.RETRY' | translate }}
                </button>
              </div>
            }
            </div>
            }
          }

        <!-- Price Summary — hidden when the user can't see prices -->
        @if (pricesVisible()) {
          <!-- banda: produtos + portes -->
          <div class="od-totals-band">
            <div class="od-tbox">
              <h4>{{ 'ORDERS.PRODUCTS' | translate }}</h4>
              <div class="od-tl"><span>{{ 'ORDERS.TOTALS.PRODUCTS_TOTAL' | translate }}</span><span class="v">{{ getTotalProducts() }}</span></div>
              <div class="od-tl" [class.disc]="discountValue('customer') > 0" [class.zero]="discountValue('customer') <= 0"><span>{{ 'ORDERS.TOTALS.CLIENT_DISCOUNT' | translate }}</span><span class="v">@if (discountValue('customer') > 0) {&minus;&nbsp;}{{ formatPrice($any(orderDetails()).customerTotalDiscount) }}</span></div>
              <div class="od-tl" [class.disc]="discountValue('vouchers') > 0" [class.zero]="discountValue('vouchers') <= 0"><span>{{ 'ORDERS.TOTALS.VOUCHER_DISCOUNT' | translate }}</span><span class="v">@if (discountValue('vouchers') > 0) {&minus;&nbsp;}{{ formatPrice($any(orderDetails()).vouchersTotalDiscount) }}</span></div>
              <div class="od-tsub"><span>{{ 'ORDERS.TOTALS.SUBTOTAL' | translate }}</span><span>{{ getSubTotal() }}</span></div>
            </div>
            <div class="od-tbox">
              <h4>{{ 'ORDERS.TOTALS.SHIPPING_COST' | translate }}</h4>
              <div class="od-tl"><span>{{ 'ORDERS.TOTALS.SHIPPING_COST' | translate }}</span><span class="v">{{ formatPrice($any(orderDetails()).shipmentType?.price) }}</span></div>
              <div class="od-tl" [class.disc]="discountValue('shipping') > 0" [class.zero]="discountValue('shipping') <= 0"><span>{{ 'ORDERS.TOTALS.SHIPPING_DISCOUNT' | translate }}</span><span class="v">@if (discountValue('shipping') > 0) {&minus;&nbsp;}{{ getShippingDiscount() }}</span></div>
              <div class="od-tl" [class.disc]="discountValue('total') > 0" [class.zero]="discountValue('total') <= 0"><span>{{ 'ORDERS.TOTALS.TOTAL_DISCOUNTS' | translate }}</span><span class="v">@if (discountValue('total') > 0) {&minus;&nbsp;}{{ formatPrice($any(orderDetails()).totalDiscount) }}</span></div>
              <div class="od-tsub"><span>{{ 'ORDERS.TOTALS.TOTAL_SHIPPING' | translate }}</span><span>{{ formatPrice($any(orderDetails()).totalShippingPrice) }}</span></div>
            </div>
          </div>

          <!-- banda: impostos + total -->
          <div class="od-totals-taxrow" [class.no-tax]="!showVatBreakdown()">
            @if (showVatBreakdown()) {
              <div class="od-tbox od-tbox-tax">
                <div class="od-tl"><span>{{ 'ORDERS.TOTALS.TOTAL_WITHOUT_VAT' | translate }}</span><span class="v">{{ formatPrice($any(orderDetails()).baseValue) }}</span></div>
                <div class="od-tl"><span>{{ 'ORDERS.TOTALS.VAT' | translate }}</span><span class="v">{{ formatPrice($any(orderDetails()).vatValue) }}</span></div>
              </div>
            }
            <div class="od-grand-box">
              <span class="lbl">{{ 'ORDERS.TOTALS.TOTAL' | translate }}</span>
              <span class="amt">{{ formatPrice($any(orderDetails()).totalValue) }}</span>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Footer -->
  <footer class="od-foot">
    @if (canCancel()) {
      <button class="od-btn od-btn-danger" (click)="cancelOrder()" [disabled]="canceling()">
        @if (canceling()) {
          <mat-spinner diameter="16"></mat-spinner>
        }
        {{ 'ORDERS.CANCEL_ORDER' | translate }}
      </button>
    }
    <button class="od-btn od-btn-secondary" (click)="duplicateOrder()" [disabled]="duplicating()">
      @if (duplicating()) {
        <mat-spinner diameter="16"></mat-spinner>
        {{ 'ORDERS.DUPLICATING' | translate }}
      } @else {
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        {{ 'ORDERS.DUPLICATE_ORDER' | translate }}
      }
    </button>
    <div class="od-spacer"></div>
    <button class="od-btn od-btn-secondary" (click)="print()">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8" rx="1"/></svg>
      {{ 'COMMON.PRINT' | translate }}
    </button>
    <button class="od-btn od-btn-primary" (click)="close()">{{ 'COMMON.CLOSE' | translate }}</button>
  </footer>

  <!-- Image Viewer Overlay -->
  @if (imageViewerOpen()) {
    <div class="image-viewer-overlay" (click)="closeImageViewer()">
      <div class="image-viewer-content" (click)="$event.stopPropagation()">
        <!-- Close button -->
        <button type="button" class="viewer-close-btn" (click)="closeImageViewer()">
          <mat-icon>close</mat-icon>
        </button>

        <!-- Navigation arrows -->
        @if (viewerImages().length > 1) {
          <button type="button" class="viewer-nav-btn prev"
                  [disabled]="currentImageIndex() === 0"
                  (click)="previousImage()">
            <mat-icon>chevron_left</mat-icon>
          </button>
          <button type="button" class="viewer-nav-btn next"
                  [disabled]="currentImageIndex() === viewerImages().length - 1"
                  (click)="nextImage()">
            <mat-icon>chevron_right</mat-icon>
          </button>
        }

        <!-- Main image -->
        <div class="viewer-image-container">
          @if (viewerImages().length > 0) {
            <img [src]="getFileImageUrl(viewerImages()[currentImageIndex()], false)"
                 [alt]="viewerImages()[currentImageIndex()]?.name || ('ORDERS.IMAGE' | translate)"
                 [matTooltip]="viewerImages()[currentImageIndex()]?.name || ('ORDERS.IMAGE' | translate)"
                 class="viewer-image">
          }
        </div>

        <!-- Image info bar -->
        <div class="viewer-info-bar">
          <span class="viewer-filename">{{ viewerImages()[currentImageIndex()]?.name || ('ORDERS.IMAGE' | translate) }}</span>
          <span class="viewer-counter">{{ currentImageIndex() + 1 }} / {{ viewerImages().length }}</span>
          <button type="button" class="viewer-download-btn" (click)="downloadFile(viewerImages()[currentImageIndex()])">
            <mat-icon>download</mat-icon>
            {{ 'COMMON.DOWNLOAD' | translate }}
          </button>
        </div>
      </div>
    </div>
  }
</div>
